import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { toast } from "react-toastify";
import { IMAGE_URL } from "../../config.js";
import { FaArrowLeft, FaDownload, FaFilePdf } from "react-icons/fa6";
import { useFileUpload } from "../../Common/PreSignUrl.jsx";
import { useRoForm } from "./RoContext.js";
import { useMutation } from "@apollo/client";
import { REGISTER_RO } from "../../graphql/Mutation/RORegister.js";

const steps = [
    { label: "Personal Info", link: "/personalinformationmanagerhub" },
    { label: "Residency Address", link: "/residencyaddmanager" },
    { label: "KYC", link: "/kycmanagerhub" },
    { label: "Bank Details", link: "/bankaccountdetailsmanagerhub" },
    { label: "Agreement", link: "/aggrementdetailsmanager" },
];

const AggrementDetailsManager = ({ currentStep = 5 }) => {
    const { handleFileUpload } = useFileUpload();
    const { formData, updateFormData } = useRoForm();
    const [registerRo, { loading, error, data }] = useMutation(REGISTER_RO);

    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            ApproveAgreement: false,
            uploadAgreement: "",
            ...formData  // prefill from context
        },
        validationSchema: Yup.object({
            ApproveAgreement: Yup.boolean().oneOf(
                [true],
                "You must approve the agreement."
            ),
            uploadAgreement: Yup.mixed().required("Upload is required"),
        }),
        onSubmit: async (values) => {
            setIsLoading(true);
            try {

                updateFormData(values);

                const finalPayload = {
                    ...formData,
                    knowEnglishLanguagePercentage: formData?.percentage,
                    telephoneNo: formData?.residentialTelephoneNo,
                    district: formData?.officeDistrict,
                    panCardImage: formData?.panFront,
                    gstLegalName: formData?.gstName,
                    getCertificate: formData?.gstFile,
                    accountNo: formData?.accountNumber,
                    accountName: formData?.accountHolder,
                    ifscCode: formData?.ifsc,
                    cancelChequeImage: formData?.cancelledCheque,
                    branchCity: formData?.city,
                    branchState: formData?.state

                };
                delete finalPayload.percentage;
                delete finalPayload.residentialTelephoneNo
                delete finalPayload.officeDistrict
                delete finalPayload.panFront
                delete finalPayload.gstName
                delete finalPayload.accountNumber
                delete finalPayload.accountHolder
                delete finalPayload.ifsc
                delete finalPayload.cancelledCheque
                delete finalPayload.city
                delete finalPayload.state
                delete finalPayload.gstFile

                const response = await registerRo({ variables: { input: finalPayload } });


                // Step 4: Show success and optio
                // Replace with API call
                // console.log("Form Submitted:", values);
                toast.success("Agreement submitted successfully!");
                updateFormData({});
            } catch (err) {
                toast.error(err?.message || "Something went wrong!");
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleStepClick = (link) => {
        navigate(link);
    };

    const handleFileChange = (event, keyPrefix, folderName) => {
        if ((!event?.target?.files || !event?.currentTarget?.files) || event?.target?.files?.length === 0) {
            setIsLoading(false);
            // console.log("===");
        }
        handleFileUpload({
            event,
            keyPrefix,
            setStateCallback: (url) => {
                const newUrl = url.split('/').slice(-2).join('/');
                // console.log('✌️newUrl --->', newUrl);
                if (keyPrefix === "uploadAgreement") {
                    formik.setFieldValue("uploadAgreement", newUrl);
                }
                setIsLoading(false);
            },
            setIsLoading,
            folderName
        });
    }

    return (
        <div className="px-4 py-6 bg-white rounded-lg mb-10 grid grid-cols-1">
            {/* Title */}
            <div className="flex items-center justify-between mb-7">
                <h1 className="font-bold text-gray-800">
                    Step {currentStep}: Agreement Verification
                </h1>
                {/* Back Button */}
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-slate-700 text-sm font-medium hover:text-slate-900"
                >
                    <FaArrowLeft /> Back
                </button>
            </div>

            {/* Stepper */}
            <div className="flex justify-between pb-6 w-full overflow-x-auto whitespace-nowrap cursor-pointer items-center relative mb-4 scrollbar-hide">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className="flex-1 text-white flex flex-col items-center flex-shrink-0 min-w-[140px] relative"
                    >
                        {/* Connector */}
                        {index !== 0 && (
                            <div className="absolute top-5 left-[-50%] w-[100%] h-0.5 bg-gray-300 z-0" />
                        )}

                        {/* Circle */}
                        <div
                            className="z-10"
                            onClick={() => handleStepClick(step.link)}
                        >
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold 
                  ${index + 1 === currentStep
                                        ? "bg-slate-900 text-white shadow-md"
                                        : "bg-white text-gray-500 border border-gray-300"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>

                        {/* Label */}
                        <span className="mt-2 text-sm text-center text-gray-700">
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Form */}
            <div className="bg-white md:border md:p-6 rounded-lg md:shadow-sm">
                <h2 className="text-lg font-bold mb-6 text-black">
                    Agreement Details
                </h2>

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Agreement Checkbox */}
                        <div className="mt-6">
                            <label className="flex items-start space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="ApproveAgreement"
                                    checked={formik.values.ApproveAgreement}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={`h-4 w-4 mt-0.5 rounded border-gray-300 focus:ring-2 
                    ${formik.touched.ApproveAgreement &&
                                            formik.errors.ApproveAgreement
                                            ? "text-red-600 focus:ring-red-500 border-red-500"
                                            : "text-slate-900 focus:ring-slate-500"
                                        }`}
                                />
                                <span className="text-sm text-gray-700">
                                    I agree to the{" "}
                                    <a
                                        href="/terms"
                                        target="_blank"
                                        className="text-blue-600 hover:underline"
                                    >
                                        Terms & Conditions
                                    </a>
                                </span>
                            </label>
                            {formik.touched.ApproveAgreement &&
                                formik.errors.ApproveAgreement && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {formik.errors.ApproveAgreement}
                                    </p>
                                )}
                        </div>

                        {/* Upload Agreement */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Upload Agreement <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="file"
                                name="uploadAgreement"
                                accept="image/*,application/pdf"
                                onChange={(e) => handleFileChange(e, "uploadAgreement", "franchiseDetails")}
                                onBlur={formik.handleBlur}
                                className={`w-full rounded-md px-3 py-2 text-sm bg-blue-50 focus:outline-none focus:ring-2 
                                        ${formik.touched.uploadAgreement &&
                                        formik.errors.uploadAgreement
                                        ? "border border-red-500 focus:ring-red-500"
                                        : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />

                            {/* Preview */}
                            {formik.values.uploadAgreement && (
                                <div className="relative w-full h-40 border rounded-lg overflow-hidden shadow-sm mt-2 flex items-center justify-center bg-gray-50">
                                    {typeof formik.values.uploadAgreement === "string" &&
                                        formik.values.uploadAgreement.endsWith(".pdf") ? (
                                        <p className="text-gray-600 text-sm flex items-center gap-2">
                                            <FaFilePdf className="text-red-600" /> Agreement PDF
                                        </p>
                                    ) : formik.values.uploadAgreement.type ===
                                        "application/pdf" ? (
                                        <p className="text-gray-600 text-sm flex items-center gap-2">
                                            <FaFilePdf className="text-red-600" /> Agreement PDF
                                        </p>
                                    ) : (
                                        <img
                                            src={
                                                typeof formik.values.uploadAgreement === "string"
                                                    ? `${IMAGE_URL}/${formik.values.uploadAgreement}`
                                                    : URL.createObjectURL(
                                                        formik.values.uploadAgreement
                                                    )
                                            }
                                            alt="Agreement Preview"
                                            className="object-cover w-full h-full"
                                        />
                                    )}

                                    {/* Download Button */}
                                    <a
                                        href={
                                            typeof formik.values.uploadAgreement === "string"
                                                ? `${IMAGE_URL}/${formik.values.uploadAgreement}`
                                                : URL.createObjectURL(formik.values.uploadAgreement)
                                        }
                                        download="agreement"
                                        className="absolute bottom-2 right-2 bg-slate-700 text-white text-xs font-medium px-2 py-1 rounded hover:bg-slate-800 flex items-center gap-1"
                                    >
                                        <FaDownload className="w-3 h-3" /> Download
                                    </a>
                                </div>
                            )}
                            {formik.touched.uploadAgreement &&
                                formik.errors.uploadAgreement && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {formik.errors.uploadAgreement}
                                    </p>
                                )}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2 rounded-lg font-semibold shadow-md transition disabled:opacity-50"
                        >
                            {isLoading ? "Submitting..." : "Submit"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default AggrementDetailsManager;
